import { Int32Value } from "google-protobuf/google/protobuf/wrappers_pb";
import {
  CategoryLimitsExtraLine as CategoryExtraLineProto,
  CategoryExtraPrice as CategoryExtraPriceProto,
  CategoryExtraRateType as CategoryExtraRateTypeProto,
  CategoryExtraUnitType as CategoryExtraUnitTypeProto,
  CategoryLimitsBlockInput as CategoryLimitsBlockInputProto,
  CategoryLimitsBlockView as CategoryLimitsBlockViewProto,
  CategoryLimitsInput as CategoryLimitsInputProto,
  GetAccommodationCategoryLimitsResponse,
} from "../../../../gen/witbooking/backoffice/accommodation/v1/accommodation_api_pb";
import {
  CategoryExtraLine,
  CategoryExtraPrice,
  ExtraRateType,
  ExtraUnitType,
} from "../../domain/entities/CategoryExtraLine";
import { ExtraCategories } from "../../domain/ExtraCategory.enum";
import { CategoryLimit } from "../../domain/entities/CategoryLimit";
import { CategoryLimits } from "../../domain/entities/CategoryLimits";
import { ExtraCategoryMapper } from "./ExtraCategoryMapper";

const unitTypeFromProtoMap: Record<CategoryExtraUnitTypeProto, ExtraUnitType> = {
  [CategoryExtraUnitTypeProto.CATEGORY_EXTRA_UNIT_TYPE_UNSPECIFIED]: ExtraUnitType.UNSPECIFIED,
  [CategoryExtraUnitTypeProto.CATEGORY_EXTRA_UNIT_TYPE_PER_STAY]: ExtraUnitType.PER_STAY,
  [CategoryExtraUnitTypeProto.CATEGORY_EXTRA_UNIT_TYPE_PER_NIGHT]: ExtraUnitType.PER_NIGHT,
};

const rateTypeFromProtoMap: Record<CategoryExtraRateTypeProto, ExtraRateType> = {
  [CategoryExtraRateTypeProto.CATEGORY_EXTRA_RATE_TYPE_UNSPECIFIED]: ExtraRateType.UNSPECIFIED,
  [CategoryExtraRateTypeProto.CATEGORY_EXTRA_RATE_TYPE_PERCENTAGE]: ExtraRateType.PERCENTAGE,
  [CategoryExtraRateTypeProto.CATEGORY_EXTRA_RATE_TYPE_FORMULA]: ExtraRateType.FORMULA,
  [CategoryExtraRateTypeProto.CATEGORY_EXTRA_RATE_TYPE_OWN]: ExtraRateType.OWN,
  [CategoryExtraRateTypeProto.CATEGORY_EXTRA_RATE_TYPE_SHARED]: ExtraRateType.SHARED,
};

/** Unset wrapper → undefined; `Int32Value(0)` → 0. */
const fromInt32 = (value: Int32Value | undefined): number | undefined => value?.getValue();

const toInt32 = (value: number | undefined): Int32Value | undefined => {
  if (value === undefined) {
    return undefined;
  }
  const wrapper = new Int32Value();
  wrapper.setValue(value);
  return wrapper;
};

export class CategoryLimitsMapper {
  static toDomain(proto: GetAccommodationCategoryLimitsResponse): CategoryLimits {
    return CategoryLimits.of({
      featureActive: proto.getFeatureActive(),
      sectionVisible: proto.getSectionVisible(),
      // A category this front does not know is left out; the save keeps its stored block.
      blocks: proto
        .getBlocksList()
        .filter(block =>
          ExtraCategories.isAssignable(ExtraCategoryMapper.toDomain(block.getCategory()))
        )
        .map(CategoryLimitsMapper.blockToDomain),
    });
  }

  /** The `category_limits` of an accommodation save: one input per block shown. */
  static toProto(limits: CategoryLimits): CategoryLimitsInputProto {
    const proto = new CategoryLimitsInputProto();
    proto.setBlocksList(limits.blocks.map(CategoryLimitsMapper.blockToProto));
    return proto;
  }

  /**
   * The stored ticks are the effective ones (the extra exists, is active, has the block's category
   * and a supported type); the block drops inactive lines.
   */
  private static blockToDomain(proto: CategoryLimitsBlockViewProto): CategoryLimit {
    return CategoryLimit.of({
      category: ExtraCategoryMapper.toDomain(proto.getCategory()),
      enabled: proto.getEnabled(),
      included: fromInt32(proto.getIncluded()),
      max: fromInt32(proto.getMax()),
      extras: proto.getExtrasList().map(CategoryLimitsMapper.lineToDomain),
      tickedExtraTickers: proto
        .getExtrasList()
        .filter(line => line.getTicked())
        .map(line => line.getTicker()),
    });
  }

  private static lineToDomain(proto: CategoryExtraLineProto): CategoryExtraLine {
    return CategoryExtraLine.of({
      extraId: proto.getExtraId(),
      ticker: proto.getTicker(),
      name: proto.getName(),
      unitType: unitTypeFromProtoMap[proto.getUnitType()] ?? ExtraUnitType.UNSPECIFIED,
      price: CategoryLimitsMapper.priceToDomain(proto.getPrice()),
      active: proto.getActive(),
      coversInventoryLine: proto.getCoversInventoryLine(),
    });
  }

  private static priceToDomain(proto: CategoryExtraPriceProto | undefined): CategoryExtraPrice {
    switch (proto?.getValueCase()) {
      case CategoryExtraPriceProto.ValueCase.CONSTANT_AMOUNT:
        return CategoryExtraPrice.constant(proto.getConstantAmount());
      case CategoryExtraPriceProto.ValueCase.RATE_TYPE:
        return CategoryExtraPrice.rate(
          rateTypeFromProtoMap[proto.getRateType()] ?? ExtraRateType.UNSPECIFIED
        );
      default:
        return CategoryExtraPrice.none();
    }
  }

  private static blockToProto(limit: CategoryLimit): CategoryLimitsBlockInputProto {
    const proto = new CategoryLimitsBlockInputProto();
    proto.setCategory(ExtraCategoryMapper.toProto(limit.category));
    proto.setEnabled(limit.enabled);
    proto.setIncluded(toInt32(limit.included));
    proto.setMax(toInt32(limit.max));
    proto.setTickedExtraTickersList(limit.tickedExtraTickers);
    return proto;
  }
}
